import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { accountsSelector } from 'state/selectors/account';
import {
  ACCOUNT_TYPES_MAP,
  FEATURE_FLAGS_NAMES,
  SettingValues
} from '@linearb/linjs-common';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { getAccounts, sendAccountInvitation } from 'state/callers';
import { ListItem } from './SearchListComponents';
import useToast from 'components/hooks/useToast';
import useSettingsService from 'components/hooks/useSettingsService';

const INVITATION_ERROR_TYPES = {
  ALREADY_REGISTERED_TO_OTHER_ORG: 'Account is already registered',
  INVITATION_ALREADY_EXISTS: 'Invitation already exists.',
  INVITATION_ALREADY_EXISTS_FOR_ANOTHER_ORGANIZATION:
    'Invitation already exists for another organization.'
};

const useEmailPreferencesData = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const accounts = useSelector(accountsSelector) || [];
  const { updateSetting } = useSettingsService();
  const onSwitchChange = async () => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.EMAIL_PREFERENCES,
      click_type: TRACKING.CLICK.TOGGLE_RESOURCE_ALLOCATION_EMAIL,
      value: !isResourceAllocationEmailEnabled
    });
    await updateIsResourceAllocationEmailEnabled(
      !isResourceAllocationEmailEnabled
    );
  };
  const updateIsResourceAllocationEmailEnabled = value =>
    updateSetting(FEATURE_FLAGS_NAMES.isResourceAllocationEmailEnabled, value);
  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.EMAIL_PREFERENCES
    });
    dispatch(getAccounts());
  }, []);
  const { getSettingOrDefinitionValue } = useSettingsService();
  const isResourceAllocationEmailEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isResourceAllocationEmailEnabled
  });

  const resourceAllocationRecipients = getSettingOrDefinitionValue(
    {
      name: SettingValues.RESOURCE_ALLOCATION_RECIPIENTS
    },
    []
  );
  const updateResourceAllocationRecipients = async recipients => {
    if (!resourceAllocationRecipients.length) {
      TrackingService.logEvent(
        TRACKING.EVENTS.CONFIGURED_RESOURCE_ALLOCATION_EMAIL,
        {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.EMAIL_PREFERENCES
        }
      );
    }
    const resolvedRecipients = recipients.map(
      ({ name, email, unsubscribe }) => ({
        name,
        email,
        ...(unsubscribe ? { unsubscribe } : {})
      })
    );
    await updateSetting(
      SettingValues.RESOURCE_ALLOCATION_RECIPIENTS,
      resolvedRecipients.length ? resolvedRecipients : null
    );
  };
  const handleInvitation = async item => {
    const { name, email } = item;
    try {
      await dispatch(
        sendAccountInvitation({
          email,
          invitee_name: name || email.split('@')[0],
          account_type_id: ACCOUNT_TYPES_MAP.Viewer
        })
      );
      await updateResourceAllocationRecipients([
        ...resourceAllocationRecipients,
        item
      ]);
    } catch (e) {
      const description = e?.response?.data?.description || '';
      if (
        description.includes(INVITATION_ERROR_TYPES.INVITATION_ALREADY_EXISTS)
      ) {
        return await updateResourceAllocationRecipients([
          ...resourceAllocationRecipients,
          item
        ]);
      }
      let message = "Can't add email.";
      if (
        description.includes(
          INVITATION_ERROR_TYPES.ALREADY_REGISTERED_TO_OTHER_ORG
        ) ||
        description.includes(
          INVITATION_ERROR_TYPES.INVITATION_ALREADY_EXISTS_FOR_ANOTHER_ORGANIZATION
        )
      ) {
        message = message +=
          ' Email already registered to another organization.';
      }
      toast({
        variant: 'error',
        message
      });
    }
  };
  const onAddItem = async item => {
    if (!accounts.some(({ email }) => email === item.email)) {
      await handleInvitation(item);
    } else {
      await updateResourceAllocationRecipients([
        ...resourceAllocationRecipients,
        item
      ]);
    }
  };
  const resubscribe = async item => {
    resourceAllocationRecipients.forEach(_item => {
      if (_item.email === item.email) {
        delete _item.unsubscribe;
      }
    });
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.EMAIL_PREFERENCES,
      click_type: TRACKING.CLICK.RESUBSCRIBE,
      email: item.email
    });
    await updateResourceAllocationRecipients(resourceAllocationRecipients);
  };

  const onRemoveItem = async item => {
    if (item.unsubscribe) {
      return resubscribe(item);
    }
    if (resourceAllocationRecipients.length === 1) {
      updateIsResourceAllocationEmailEnabled(false);
    }
    await updateResourceAllocationRecipients(
      resourceAllocationRecipients.filter(({ email }) => email !== item.email)
    );
  };
  const items = _.sortBy(accounts, ({ name }) => name.toLowerCase()).map(
    contributor => {
      const { email, name, avatar_url: avatarUrl } = contributor;
      return {
        ...contributor,
        avatarUrl,
        view: (
          <ListItem
            key={email}
            name={name}
            email={email}
            avatarUrl={avatarUrl}
          />
        )
      };
    }
  );
  return {
    items,
    onAddItem,
    onRemoveItem,
    onSwitchChange,
    isResourceAllocationEmailEnabled,
    resourceAllocationRecipients
  };
};
export default useEmailPreferencesData;
